/**
 * Offline Sync Queue
 * Stores mutations in IndexedDB when offline and syncs when back online
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { logger } from './logger';
import api from './api';

interface SyncQueueItem {
  id: string;
  type: 'order' | 'cart' | 'chat' | 'promo';
  action: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: any;
  timestamp: number;
  retries: number;
  error?: string;
}

interface SyncQueueDB extends DBSchema {
  queue: {
    key: string;
    value: SyncQueueItem;
    indexes: { 'by-timestamp': number };
  };
}

class SyncQueue {
  private db: IDBPDatabase<SyncQueueDB> | null = null;
  private isSyncing = false;
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private listeners: Set<() => void> = new Set();

  async init() {
    try {
      this.db = await openDB<SyncQueueDB>('buyer-sync-queue', 1, {
        upgrade(db) {
          const store = db.createObjectStore('queue', { keyPath: 'id' });
          store.createIndex('by-timestamp', 'timestamp');
        },
      });

      logger.info('Sync queue initialized');

      // Start auto-sync every 30 seconds
      this.startAutoSync();

      // Listen for online event
      window.addEventListener('online', () => this.sync());
    } catch (error) {
      logger.error('Failed to initialize sync queue', error as Error);
    }
  }

  private startAutoSync() {
    if (this.syncInterval) return;

    this.syncInterval = setInterval(() => {
      if (navigator.onLine) {
        this.sync();
      }
    }, 30000); // Every 30 seconds
  }

  private stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Add item to sync queue
   */
  async add(item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    if (!this.db) {
      throw new Error('Sync queue not initialized');
    }

    const queueItem: SyncQueueItem = {
      ...item,
      id: `${item.type}_${item.action}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retries: 0,
    };

    await this.db.add('queue', queueItem);
    logger.info('Added to sync queue', { id: queueItem.id, type: queueItem.type, action: queueItem.action });

    this.notifyListeners();

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.sync();
    }
  }

  /**
   * Get all pending items
   */
  async getAll(): Promise<SyncQueueItem[]> {
    if (!this.db) return [];
    return this.db.getAllFromIndex('queue', 'by-timestamp');
  }

  /**
   * Get queue count
   */
  async count(): Promise<number> {
    if (!this.db) return 0;
    return this.db.count('queue');
  }

  /**
   * Remove item from queue
   */
  async remove(id: string): Promise<void> {
    if (!this.db) return;
    await this.db.delete('queue', id);
    this.notifyListeners();
  }

  /**
   * Sync all pending items
   */
  async sync(): Promise<void> {
    if (this.isSyncing || !navigator.onLine || !this.db) return;

    this.isSyncing = true;
    logger.info('Starting sync...');

    try {
      const items = await this.getAll();

      if (items.length === 0) {
        logger.info('Sync queue empty, nothing to sync');
        return;
      }

      logger.info(`Syncing ${items.length} items...`);

      for (const item of items) {
        try {
          await this.syncItem(item);
          await this.remove(item.id);
          logger.info('Item synced successfully', { id: item.id });
        } catch (error) {
          logger.error('Failed to sync item', error as Error, { id: item.id });

          // Update retry count
          item.retries++;
          item.error = (error as Error).message;

          // Remove if too many retries (max 5)
          if (item.retries >= 5) {
            logger.warn('Max retries reached, removing from queue', { id: item.id });
            await this.remove(item.id);
          } else {
            // Update item in queue
            await this.db!.put('queue', item);
          }
        }
      }

      logger.info('Sync complete');
    } catch (error) {
      logger.error('Sync failed', error as Error);
    } finally {
      this.isSyncing = false;
      this.notifyListeners();
    }
  }

  /**
   * Sync individual item
   */
  private async syncItem(item: SyncQueueItem): Promise<void> {
    const { method, endpoint, data } = item;

    switch (method) {
      case 'GET':
        await api.get(endpoint);
        break;
      case 'POST':
        await api.post(endpoint, data);
        break;
      case 'PUT':
        await api.put(endpoint, data);
        break;
      case 'PATCH':
        await api.patch(endpoint, data);
        break;
      case 'DELETE':
        await api.delete(endpoint);
        break;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  }

  /**
   * Subscribe to queue changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Clear all items (for testing)
   */
  async clear(): Promise<void> {
    if (!this.db) return;
    await this.db.clear('queue');
    this.notifyListeners();
  }

  /**
   * Destroy sync queue
   */
  async destroy() {
    this.stopAutoSync();
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

export const syncQueue = new SyncQueue();
export default syncQueue;

















